/**
 * Public seller vehicle submission API client (Sell Your Car M5).
 */
import { API_BASE_URL } from "../config/apiBaseUrl";
import {
  SELLER_SUBMISSION_API_PATH,
  buildSellerSubmissionAttribution,
  buildSellerSubmissionPayload,
  getSellerResultMessage,
  normalizeSellerSubmissionResult,
  releaseSellerSubmissionToken,
  SELLER_SUBMISSION_STATUSES,
  tryAcquireSellerSubmissionToken,
} from "../utils/sellerIntake";
import { buildClientInFlightDuplicateResult } from "../utils/sellerIntake/sellerSubmissionGuardrails";
import { mapSellerSubmissionApiErrorsToFormErrors } from "../utils/sellerIntake/sellerIntakeValidation";

/**
 * @typedef {import('../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionInput} SellerSubmissionInput
 * @typedef {import('../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionPayload} SellerSubmissionPayload
 * @typedef {import('../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionResult} SellerSubmissionResult
 */

/**
 * @param {SellerSubmissionPayload|object} payload
 * @param {{ fetchFn?: typeof fetch }} [options]
 * @returns {Promise<SellerSubmissionResult & { formErrors?: Record<string, string> }>}
 */
export async function submitSellerSubmission(payload, options = {}) {
  const { fetchFn = fetch } = options;

  try {
    const response = await fetchFn(`${API_BASE_URL}${SELLER_SUBMISSION_API_PATH}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    let body = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    if (response.ok && body?.status === SELLER_SUBMISSION_STATUSES.RECEIVED) {
      return {
        ...normalizeSellerSubmissionResult({ ok: true, ...body }),
        uploadToken: body.uploadToken || null,
        formErrors: {},
      };
    }

    const result = normalizeSellerSubmissionResult({
      status: response.status,
      body,
      fieldErrors: body?.fieldErrors || body?.errors,
      statusKey: body?.status,
      spam: body?.status === SELLER_SUBMISSION_STATUSES.SPAM_REJECTED,
    });

    const formErrors = mapSellerSubmissionApiErrorsToFormErrors(result.fieldErrors);
    if (!formErrors.form && result.sellerMessage) {
      formErrors.form = result.sellerMessage;
    }

    return {
      ...result,
      formErrors,
    };
  } catch (error) {
    const result = normalizeSellerSubmissionResult(error);
    return {
      ...result,
      formErrors: {
        form: result.sellerMessage || getSellerResultMessage(SELLER_SUBMISSION_STATUSES.NETWORK_ERROR),
      },
    };
  }
}

/**
 * @param {SellerSubmissionInput|object} input
 * @param {{
 *   attribution?: object,
 *   attributionOptions?: object,
 *   fetchFn?: typeof fetch,
 * }} [options]
 * @returns {Promise<SellerSubmissionResult & { formErrors?: Record<string, string> }>}
 */
export async function submitSellerVehicleIntake(input, options = {}) {
  const submissionToken = options.submissionToken || null;

  if (submissionToken && !tryAcquireSellerSubmissionToken(submissionToken)) {
    return buildClientInFlightDuplicateResult();
  }

  const attribution = options.attribution || buildSellerSubmissionAttribution(options.attributionOptions);
  const payload = buildSellerSubmissionPayload(input, attribution, {
    submittedAt: new Date().toISOString(),
    submissionToken,
  });

  try {
    const result = await submitSellerSubmission(payload, options);
    if (submissionToken) {
      releaseSellerSubmissionToken(submissionToken, { succeeded: result.ok === true });
    }
    return result;
  } catch (error) {
    if (submissionToken) {
      releaseSellerSubmissionToken(submissionToken);
    }
    throw error;
  }
}
